import { Space } from 'antd'
import Link from 'next/link'
import { FunctionComponent } from 'react'
import styled, { css, SimpleInterpolation } from 'styled-components'
import { useQueryParams } from 'use-query-params'
import { useEnterpriseIdentity } from '@/components/common/contexts/enterprise-identity-context'
import RWDCardLayout, {
  CommonCardDescription,
  CommonCardTitle,
} from '@/components/layout/rwd-card-layout'
import {
  RegisterIdentityAdOption,
  RegisterIdentityKolOption,
} from '@/components/register/register-identity-option'
import routes from '@/hooks/use-authorization/routes'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { useI18n } from '@/hooks/use-i18n'
import { useIntl } from '@/i18n/hooks/use-intl'
import { ampli } from '@/utils/ampli'
import { NonNullStringParam } from '@/utils/use-query-params'

export const RegisterEntryCard: FunctionComponent = () => {
  const enterpriseIdentity = useEnterpriseIdentity()

  const { formatMessage } = useIntl()
  const { locale } = useI18n()
  const [{ from }] = useQueryParams({
    from: NonNullStringParam,
  })

  const breakpoint = useBreakpoint()
  const isPC = breakpoint === 'PC'

  const handleAdOptionClick = (): void => {
    ampli.confirmIdentityChoice({ identity: 'ad' })
    routes.registerAd.goto({}, { lang: locale, from })
  }

  const handleKolOptionClick = (): void => {
    ampli.confirmIdentityChoice({ identity: 'kol' })
    routes.registerKol.goto({}, { lang: locale, from })
  }

  const contentEl = (
    <Container $isPC={isPC}>
      <Space align='center' direction='vertical' size='small'>
        <CommonCardTitle>
          {formatMessage({ id: 'account:register_entry_title' })}
        </CommonCardTitle>
        <CommonCardDescription>
          {formatMessage({
            id: 'account:register_entry_description',
          })}
        </CommonCardDescription>
      </Space>
      <OptionsContainer $isPC={isPC}>
        <RegisterIdentityAdOption onClick={handleAdOptionClick} />
        {!enterpriseIdentity && (
          <RegisterIdentityKolOption onClick={handleKolOptionClick} />
        )}
      </OptionsContainer>
      {!isPC && (
        <CommonCardDescription>
          {formatMessage({ id: 'account:already_has_account' })}
          <Link href={routes.login.stringifyUrl({}, { lang: locale })}>
            {formatMessage({ id: 'account:register_login_entry' })}
          </Link>
        </CommonCardDescription>
      )}
    </Container>
  )

  return <RWDCardLayout>{contentEl}</RWDCardLayout>
}

const PCContainerStyle = css`
  gap: 36px;
`

const MobileContainerStyle = css`
  gap: 32px;
`

const Container = styled.div<{ $isPC: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 36px;

  ${({ $isPC }): SimpleInterpolation =>
    $isPC ? PCContainerStyle : MobileContainerStyle}
`

const PCOptionsContainerStyle = css`
  gap: 36px;
  text-align: center;
  > div {
    flex: 1;
  }
`

const MobileOptionsContainerStyle = css`
  gap: 24px;
  flex-direction: column;
  text-align: left;
`

const OptionsContainer = styled.div<{
  $isPC: boolean
}>`
  display: flex;

  ${({ $isPC }): SimpleInterpolation =>
    $isPC ? PCOptionsContainerStyle : MobileOptionsContainerStyle}
`
